import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, User, Clock, AlertCircle, ArrowLeft } from 'lucide-react';
import { getChatRooms } from '../lib/chat';
import { supabase } from '../lib/supabase';
import LoadingIndicator from '../components/LoadingIndicator';

const ChatList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatRooms, setChatRooms] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        if (!user) {
          navigate('/login');
          return;
        }
        setCurrentUser(user);

        // Get chat rooms
        const rooms = await getChatRooms();
        setChatRooms(rooms || []);

        // Subscribe to new messages
        const messagesSubscription = supabase
          .channel('messages')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'messages'
            },
            async (payload) => {
              // When a new message arrives, refresh the chat rooms to get latest messages
              const updatedRooms = await getChatRooms();
              setChatRooms(updatedRooms || []);
            }
          )
          .subscribe();

        // Subscribe to chat room updates
        const roomsSubscription = supabase
          .channel('chat_rooms')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'chat_rooms'
            },
            async () => {
              // When chat rooms change, refresh the list
              const updatedRooms = await getChatRooms();
              setChatRooms(updatedRooms || []);
            }
          )
          .subscribe();

        return () => {
          messagesSubscription.unsubscribe();
          roomsSubscription.unsubscribe();
        };
      } catch (err) {
        console.error('Error initializing chat list:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chats');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [navigate]);

  if (loading) {
    return <LoadingIndicator message="Loading your chats..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition flex items-center justify-center"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Chats</h1>
        </div>

        {chatRooms.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Noch keine Nachrichten</h3>
            <p className="text-gray-600">
              Starte einen Konversation um zu chatten zu beginnen 
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {chatRooms.map((room) => {
              const otherParticipant = room.participant1_id === currentUser?.id ? room.participant2 : room.participant1;
              const latestMessage = room.latest_message?.[0];

              return (
                <Link
                  key={room.id}
                  to={`/chat/${room.id}`}
                  className="block p-4 hover:bg-gray-50 transition"
                >
                  <div className="flex items-center space-x-4">
                    {otherParticipant?.profile_pictures?.avatar ? (
                      <img
                        src={otherParticipant.profile_pictures.avatar}
                        alt={otherParticipant.full_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 truncate">
                          {otherParticipant?.full_name || 'Unknown User'}
                        </h2>
                        {latestMessage && (
                          <span className="text-sm text-gray-500">
                            <Clock className="h-4 w-4 inline mr-1" />
                            {new Date(latestMessage.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      {latestMessage && (
                        <p className="text-gray-600 truncate">
                          {latestMessage.sender.id === currentUser?.id ? (
                            <span className="text-gray-400">You: </span>
                          ) : null}
                          {latestMessage.content}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;